import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, Chip, Grid, Typography } from '@mui/material';
import { Person as PersonIcon, Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as S from '../orders/ExchangeModal/ExchangeDrawer.styles';
import {
  drawerSectionCardSx,
  drawerSectionTitleSx,
} from '../../crm/client-management/clientCreateDrawer.styles';
import ClientCreateDrawerSalesHistory from '../../crm/client-management/ClientCreateDrawerSalesHistory';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { sellersService, type SellerListRow } from '../../../services/sellers.service';
import type { CustomerSalesHistoryItem } from '../../../types/sales.types';

const HISTORY_PAGE_SIZE = 10;

export interface SellerDetailDrawerProps {
  open: boolean;
  sellerId: string | null;
  onClose: () => void;
}

const SellerDetailDrawer: React.FC<SellerDetailDrawerProps> = ({ open, sellerId, onClose }) => {
  const navigate = useNavigate();
  const [seller, setSeller] = useState<SellerListRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [historyItems, setHistoryItems] = useState<CustomerSalesHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  useEscapeKey(() => {
    if (open) onClose();
  }, open);

  const loadHistory = useCallback(
    async (page: number, activeSellerId: string) => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const res = await sellersService.getSalesHistory(activeSellerId, {
          page,
          limit: HISTORY_PAGE_SIZE,
        });
        if (sellerId !== activeSellerId) {
          return;
        }
        setHistoryItems(res.data);
        setHistoryPage(res.page);
        setHistoryTotal(res.total);
      } catch (e) {
        if (sellerId !== activeSellerId) {
          return;
        }
        setHistoryError(
          e instanceof Error ? e.message : 'No se pudo cargar el historial de ventas.',
        );
      } finally {
        if (sellerId === activeSellerId) {
          setHistoryLoading(false);
        }
      }
    },
    [sellerId],
  );

  useEffect(() => {
    if (!open || sellerId == null) {
      setSeller(null);
      setHistoryItems([]);
      setHistoryTotal(0);
      setHistoryPage(1);
      setLoadError(null);
      setHistoryError(null);
      return;
    }

    let cancelled = false;
    const activeSellerId = sellerId;

    setSeller(null);
    setHistoryItems([]);
    setHistoryTotal(0);
    setHistoryPage(1);
    setLoading(true);
    setLoadError(null);
    setHistoryError(null);

    sellersService
      .getById(activeSellerId)
      .then((s) => {
        if (!cancelled && sellerId === activeSellerId) {
          setSeller(s);
        }
      })
      .catch((e) => {
        if (!cancelled && sellerId === activeSellerId) {
          setLoadError(e instanceof Error ? e.message : 'No se pudo cargar el vendedor.');
        }
      })
      .finally(() => {
        if (!cancelled && sellerId === activeSellerId) {
          setLoading(false);
        }
      });

    void loadHistory(1, activeSellerId);

    return () => {
      cancelled = true;
    };
  }, [open, sellerId, loadHistory]);

  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));

  const handleGoToOrder = useCallback(
    (orderId: number) => {
      navigate('/dashboard/orders', { state: { openOrderDetails: orderId } });
    },
    [navigate],
  );

  const sellerInitials =
    seller != null
      ? `${seller.firstName.charAt(0) ?? ''}${seller.lastName.charAt(0) ?? ''}`.trim() || '?'
      : '?';

  if (!open) return null;

  return (
    <S.StyledDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: '720px', md: '780px' }, maxWidth: '780px' } }}
    >
      <S.DrawerContainer>
        <S.DrawerHeader
          sx={{
            background: 'linear-gradient(to right, #4f46e5, #a21caf)',
            borderBottom: 'none',
            color: '#ffffff',
          }}
        >
          <S.DrawerTitle sx={{ color: '#ffffff' }}>
            <PersonIcon sx={{ fontSize: 24, color: '#ffffff' }} />
            Detalle de vendedor
          </S.DrawerTitle>
          <S.CloseButton
            onClick={onClose}
            sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon />
          </S.CloseButton>
        </S.DrawerHeader>

        <S.ContentContainer>
          {loading ? (
            <Typography color="text.secondary">Cargando…</Typography>
          ) : loadError ? (
            <Typography color="error">{loadError}</Typography>
          ) : seller ? (
            <>
              <S.SectionBox sx={(theme) => drawerSectionCardSx(theme)}>
                <S.SectionHeader>
                  <InfoIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
                  <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
                    Información del vendedor
                  </Typography>
                </S.SectionHeader>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, fontSize: 22 }}>
                      {sellerInitials}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {seller.firstName} {seller.lastName}
                      </Typography>
                      <Chip
                        size="small"
                        label={seller.isActive ? 'Activo' : 'Inactivo'}
                        color={seller.isActive ? 'success' : 'default'}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Correo
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {seller.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Teléfono
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {(seller.phone?.trim() ?? '') !== '' ? seller.phone!.trim() : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Sucursal
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {seller.locationName ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total ventas / N ventas
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {new Intl.NumberFormat('es-BO', {
                          style: 'currency',
                          currency: 'BOB',
                          maximumFractionDigits: 2,
                        }).format(seller.totalSales)}{' '}
                        · {seller.salesCount} venta(s)
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </S.SectionBox>

              <ClientCreateDrawerSalesHistory
                historyItems={historyItems}
                historyLoading={historyLoading}
                historyError={historyError}
                historyPage={historyPage}
                historyTotal={historyTotal}
                historyTotalPages={historyTotalPages}
                onRetry={() => {
                  if (sellerId != null) {
                    void loadHistory(historyPage, sellerId);
                  }
                }}
                onGoToOrder={handleGoToOrder}
                onPrevPage={() => {
                  if (sellerId != null) {
                    void loadHistory(Math.max(1, historyPage - 1), sellerId);
                  }
                }}
                onNextPage={() => {
                  if (sellerId != null) {
                    void loadHistory(Math.min(historyTotalPages, historyPage + 1), sellerId);
                  }
                }}
              />
            </>
          ) : null}
        </S.ContentContainer>
      </S.DrawerContainer>
    </S.StyledDrawer>
  );
};

export default SellerDetailDrawer;
